import React, { useState } from 'react';
import { X, Check, Minus, Crown, Rocket, Lightning, ShieldCheck, Headset, ChartBar, Users, Buildings, Star, Eye, Bell, FileText, ChatCircle, MagnifyingGlass, MapPin, Code, SpinnerGap } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { paymentsAPI } from '../api/api';
import { toast } from '../hooks/use-toast';
import { openCheckout } from '../utils/checkout';

// Complete plan data with all features for comparison
const WORKER_PLANS_DATA = [
  {
    id: 'free_worker',
    name: 'Free',
    price: 0,
    period: '/month',
    description: 'Perfect for getting started',
    color: 'gray',
    icon: Star,
    popular: false
  },
  {
    id: 'pro_worker',
    name: 'Pro',
    price: 19.99,
    period: '/month',
    description: 'Best for active professionals',
    color: 'blue',
    icon: Rocket,
    popular: true,
    badge: 'Most Popular'
  },
];

const CONTRACTOR_PLANS_DATA = [
  {
    id: 'starter_contractor',
    name: 'Starter',
    price: 0,
    period: '/month',
    description: 'Try the platform risk-free',
    color: 'gray',
    icon: Star,
    popular: false
  },
  {
    id: 'business_contractor',
    name: 'Pro',
    price: 79,
    period: '/month',
    description: 'Everything unlocked for construction companies',
    color: 'blue',
    icon: Buildings,
    popular: true,
    badge: 'Most Popular'
  },
];

// Worker features with values per plan
const WORKER_FEATURES = [
  {
    category: 'Job Applications',
    features: [
      { name: 'Monthly Applications', free: '5/month', pro: 'Unlimited', elite: 'Unlimited' },
      { name: 'Application Priority', free: false, pro: true, elite: true },
      { name: 'Instant Job Alerts', free: false, pro: true, elite: true },
    ]
  },
  {
    category: 'Profile & Visibility',
    features: [
      { name: 'Profile Creation', free: true, pro: true, elite: true },
      { name: 'Search Visibility', free: 'Standard', pro: '3x Higher', elite: 'Featured (Top)' },
      { name: 'Featured Profile', free: false, pro: false, elite: true },
      { name: 'Verified Badge', free: false, pro: false, elite: true },
      { name: 'Profile Boost', free: false, pro: false, elite: '2x/week' },
    ]
  },
  {
    category: 'Communication',
    features: [
      { name: 'Messaging', free: 'Basic', pro: 'Priority', elite: 'Priority' },
      { name: 'Response Priority', free: false, pro: true, elite: true },
    ]
  },
  {
    category: 'Analytics & Tools',
    features: [
      { name: 'Profile Analytics', free: false, pro: true, elite: 'Advanced' },
      { name: 'Contract Templates', free: false, pro: false, elite: true },
      { name: 'Portfolio Showcase', free: true, pro: true, elite: 'Enhanced' },
    ]
  },
  {
    category: 'Support',
    features: [
      { name: 'Email Support', free: true, pro: true, elite: true },
      { name: 'Priority Support', free: false, pro: true, elite: '24/7' },
      { name: 'Dedicated Account Manager', free: false, pro: false, elite: true },
    ]
  }
];

// Contractor features with values per plan
const CONTRACTOR_FEATURES = [
  {
    category: 'Job Postings',
    features: [
      { name: 'Monthly Job Posts', starter: '3/month', business: 'Unlimited', enterprise: 'Unlimited' },
      { name: 'Featured Listings', starter: false, business: '5/month', enterprise: 'Unlimited' },
      { name: 'Job Visibility Boost', starter: false, business: true, enterprise: true },
    ]
  },
  {
    category: 'Worker Search',
    features: [
      { name: 'Worker Search', starter: 'Basic', business: 'Advanced', enterprise: 'Advanced' },
      { name: 'Advanced Filtering', starter: false, business: true, enterprise: true },
      { name: 'Worker Matching AI', starter: false, business: true, enterprise: true },
    ]
  },
  {
    category: 'Team & Collaboration',
    features: [
      { name: 'Team Members', starter: '1 user', business: '5 users', enterprise: 'Unlimited' },
      { name: 'Team Collaboration Tools', starter: false, business: true, enterprise: true },
      { name: 'Multi-Location Management', starter: false, business: false, enterprise: true },
    ]
  },
  {
    category: 'Analytics & Integrations',
    features: [
      { name: 'Hiring Analytics', starter: false, business: true, enterprise: 'Advanced' },
      { name: 'Cost Tracking', starter: true, business: true, enterprise: true },
      { name: 'API Access', starter: false, business: false, enterprise: true },
      { name: 'Custom Integrations', starter: false, business: false, enterprise: true },
    ]
  },
  {
    category: 'Pricing & Support',
    features: [
      { name: 'Platform Commission', starter: '10%', business: '8%', enterprise: '5%' },
      { name: 'Email Support', starter: true, business: true, enterprise: true },
      { name: 'Priority Support', starter: false, business: true, enterprise: '24/7' },
      { name: 'Dedicated Account Manager', starter: false, business: false, enterprise: true },
    ]
  }
];

const FeatureValue = ({ value }) => {
  if (value === true) {
    return <Check size={20} weight="bold" className="text-emerald-500 mx-auto" />;
  }
  if (value === false) {
    return <Minus size={20} className="text-neutral-300 dark:text-neutral-600 mx-auto" />;
  }
  return <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">{value}</span>;
};

const PlanComparisonModal = ({ isOpen, onClose, userType = 'worker', currentPlan = null }) => {
  const [loadingPlan, setLoadingPlan] = useState(null);
  
  if (!isOpen) return null;

  const plansData = userType === 'worker' ? WORKER_PLANS_DATA : CONTRACTOR_PLANS_DATA;
  const featuresData = userType === 'worker' ? WORKER_FEATURES : CONTRACTOR_FEATURES;
  const planKeys = userType === 'worker' ? ['free', 'elite'] : ['starter', 'enterprise'];

  const handleSelectPlan = async (planId) => {
    // Don't process free plans or current plan
    if (planId === currentPlan) {
      toast({ title: 'Already on this plan', description: 'You are currently subscribed to this plan.' });
      return;
    }
    
    if (planId === 'free_worker' || planId === 'starter_contractor') {
      toast({ title: 'Free Plan', description: 'You\'re already on the free tier or can downgrade from account settings.' });
      return;
    }

    setLoadingPlan(planId);
    try {
      const response = await paymentsAPI.createCheckout({
        product_id: planId,
        product_type: 'subscription',
        origin_url: window.location.href
      });
      
      if (response.data.checkout_url) {
        openCheckout(response.data.checkout_url);
      }
    } catch (error) {
      toast({
        title: 'Checkout Error',
        description: error.response?.data?.detail || 'Failed to start checkout',
        variant: 'destructive'
      });
    } finally {
      setLoadingPlan(null);
    }
  };

  const getColorClasses = (color, isHeader = false) => {
    const colors = {
      gray: isHeader ? 'bg-neutral-100 dark:bg-neutral-800' : 'border-neutral-300',
      blue: isHeader ? 'bg-blue-50 dark:bg-blue-900/30' : 'border-blue-400',
      amber: isHeader ? 'bg-amber-50 dark:bg-amber-900/30' : 'border-amber-400'
    };
    return colors[color] || colors.gray;
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div 
        className="bg-white dark:bg-neutral-900 rounded-2xl border-2 border-black dark:border-neutral-700 shadow-[8px_8px_0px_#000] dark:shadow-[8px_8px_0px_#2B4C6F] w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-[#2B4C6F] to-[#203A55] text-white p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-black">Compare Plans</h2>
            <p className="text-white/80 mt-1">
              {userType === 'worker' ? 'Find the perfect plan for your career' : 'Scale your hiring with the right plan'}
            </p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X size={24} weight="bold" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Plan Cards Header */}
          <div className="grid grid-cols-3 gap-4 mb-6 sticky top-0 bg-white dark:bg-neutral-900 pb-4 z-10">
            <div className="flex items-end">
              <span className="text-sm font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Features</span>
            </div>
            {plansData.map((plan) => {
              const PlanIcon = plan.icon;
              const isCurrentPlan = plan.id === currentPlan;
              
              return (
                <div 
                  key={plan.id}
                  className={`rounded-xl border-2 ${plan.popular ? 'border-blue-500 dark:border-blue-400' : 'border-black dark:border-neutral-700'} ${getColorClasses(plan.color, true)} p-4 relative`}
                >
                  {plan.badge && (
                    <Badge className={`absolute -top-3 left-1/2 -translate-x-1/2 ${plan.color === 'blue' ? 'bg-blue-500' : 'bg-amber-500'} text-white border-0`}>
                      {plan.badge}
                    </Badge>
                  )}
                  {isCurrentPlan && (
                    <Badge className="absolute -top-3 right-2 bg-emerald-500 text-white border-0">
                      Current
                    </Badge>
                  )}
                  <div className="text-center">
                    <div className={`w-12 h-12 mx-auto mb-2 rounded-xl flex items-center justify-center ${plan.color === 'gray' ? 'bg-neutral-200 dark:bg-neutral-700' : plan.color === 'blue' ? 'bg-blue-100 dark:bg-blue-800' : 'bg-amber-100 dark:bg-amber-800'}`}>
                      <PlanIcon size={24} weight="bold" className={plan.color === 'gray' ? 'text-neutral-600 dark:text-neutral-300' : plan.color === 'blue' ? 'text-blue-600 dark:text-blue-300' : 'text-amber-600 dark:text-amber-300'} />
                    </div>
                    <h3 className="font-black text-lg text-neutral-900 dark:text-white">{plan.name}</h3>
                    <div className="mt-2">
                      <span className="text-3xl font-black text-neutral-900 dark:text-white">${plan.price}</span>
                      <span className="text-neutral-500 dark:text-neutral-400">{plan.period}</span>
                    </div>
                    <Button
                      onClick={() => handleSelectPlan(plan.id)}
                      disabled={loadingPlan === plan.id || isCurrentPlan}
                      className={`w-full mt-3 ${
                        isCurrentPlan 
                          ? 'bg-emerald-500 hover:bg-emerald-500 cursor-default' 
                          : plan.popular 
                            ? 'bg-blue-600 hover:bg-blue-700' 
                            : 'bg-neutral-800 hover:bg-neutral-700 dark:bg-neutral-700 dark:hover:bg-neutral-600'
                      } text-white font-bold border-2 border-black shadow-[2px_2px_0px_#000] active:translate-y-0.5 active:shadow-none`}
                      data-testid={`select-plan-${plan.id}`}
                    >
                      {loadingPlan === plan.id ? (
                        <SpinnerGap size={20} className="animate-spin" />
                      ) : isCurrentPlan ? (
                        'Current Plan'
                      ) : plan.price === 0 ? (
                        'Free'
                      ) : (
                        'Select Plan'
                      )}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Feature Comparison Table */}
          <div className="space-y-6">
            {featuresData.map((category, catIdx) => (
              <div key={catIdx} className="border-2 border-black dark:border-neutral-700 rounded-xl overflow-hidden">
                {/* Category Header */}
                <div className="bg-neutral-100 dark:bg-neutral-800 px-4 py-3 border-b-2 border-black dark:border-neutral-700">
                  <h4 className="font-black text-neutral-900 dark:text-white">{category.category}</h4>
                </div>
                
                {/* Features */}
                <div className="divide-y-2 divide-neutral-200 dark:divide-neutral-700">
                  {category.features.map((feature, featIdx) => (
                    <div key={featIdx} className="grid grid-cols-3 gap-4 px-4 py-3 hover:bg-neutral-50 dark:hover:bg-neutral-800/50">
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">{feature.name}</span>
                      </div>
                      {planKeys.map((key) => (
                        <div key={key} className="flex items-center justify-center">
                          <FeatureValue value={feature[key]} />
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="mt-8 text-center p-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border-2 border-blue-200 dark:border-blue-800">
            <h3 className="text-xl font-black text-neutral-900 dark:text-white mb-2">
              Need help choosing?
            </h3>
            <p className="text-neutral-600 dark:text-neutral-400 mb-4">
              Our team can help you find the perfect plan for your needs.
            </p>
            <Button 
              variant="outline"
              className="border-2 border-blue-500 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 font-bold"
            >
              <Headset size={20} className="mr-2" />
              Contact Support
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanComparisonModal;
